/**
 * Datos de DEMO del Vault para el workshop guiado (/vault/workshop).
 *
 * Un Vault entero en memoria: credenciales, accesos, historial, links y
 * auditoría, con la persona que mira como protagonista (owner de varias).
 * Lo consume `demo-link.ts`, que responde `vault.*` desde acá en vez de
 * pegarle a la Lambda del Vault — nadie toca credenciales reales, y una
 * rotación o un grant de la demo se olvidan al cerrar el workshop.
 *
 * Las formas de las respuestas siguen a shared/types/vault.ts y a los routers
 * (backend/src/routers/vault.ts); si un router cambia, esto se tipa contra
 * RouterOutputs en demo-link.ts y falla el typecheck.
 */
import { clearVaultUnlockToken, getVaultUnlockToken, setVaultUnlockToken } from '@/lib/vault-session';
import { effectivePermission, principalsFor, type PrincipalSet } from '../../../../shared/vault-permissions';
import {
  VAULT_AREA_LABEL,
  VAULT_POLICY,
  areaGroupPrincipal,
  userPrincipal,
  type VaultAuditAction,
  type VaultAuditEvent,
  type VaultGrant,
  type VaultPermission,
  type VaultSecretMeta,
} from '../../../../shared/types/vault';
import type { DemoOp } from '@/components/vault/workshop/types';
import { linkStatus } from '../../../../shared/vault-link-status';

export const DEMO_UNLOCK_TOKEN = 'demo-workshop-unlock';
const DEMO_IP = '181.44.12.7';

interface DemoSecret extends Omit<VaultSecretMeta, 'myPermission'> {
  password: string;
  notes: string | null;
  history: Array<{ version: number; rotatedAt: string; rotatedBy: string; password: string }>;
}

interface DemoLink {
  linkId: string;
  secretId: string;
  secretName: string;
  createdBy: string;
  createdAt: string;
  expiresAt: number;
  expiresAtIso: string;
  maxViews: number | null;
  viewCount: number;
  lastViewedAt?: string;
  lastViewedIp?: string;
  revokedAt?: string;
  revokedBy?: string;
  includesTotp: boolean;
}

export interface DemoState {
  me: string;
  mfaEnrolled: boolean;
  hasPassphrase: boolean;
  secrets: DemoSecret[];
  grants: VaultGrant[];
  links: DemoLink[];
  activity: VaultAuditEvent[];
}

const daysAgo = (d: number, h = 0) => new Date(Date.now() - d * 86_400_000 - h * 3_600_000).toISOString();
const hoursAgo = (h: number) => daysAgo(0, h);
let seq = 0;
const nextId = (p: string) => `${p}-${(++seq).toString(36)}${Date.now().toString(36).slice(-3)}`;

function secret(
  id: string,
  name: string,
  username: string,
  o: { area?: string; clientId?: string; url?: string; hasTotp?: boolean; owner: string; rotatedDaysAgo: number; needsRotation?: boolean; tags?: string[]; notes?: string | null; password?: string }
): DemoSecret {
  const created = daysAgo(o.rotatedDaysAgo + 40);
  return {
    secretId: id,
    name,
    username,
    url: o.url,
    area: o.area,
    clientId: o.clientId,
    tags: o.tags,
    hasTotp: !!o.hasTotp,
    ownerEmail: o.owner,
    needsRotation: !!o.needsRotation,
    lastRotatedAt: daysAgo(o.rotatedDaysAgo),
    createdAt: created,
    createdBy: o.owner,
    updatedAt: daysAgo(o.rotatedDaysAgo),
    updatedBy: o.owner,
    version: 3,
    password: o.password ?? 'Wc!9tR-q2Lm#7vXp',
    notes: o.notes ?? null,
    history: [{ version: 2, rotatedAt: daysAgo(o.rotatedDaysAgo + 95), rotatedBy: o.owner, password: 'anterior-8Ks#2q' }],
  };
}

/**
 * @param fresh  arrancar como persona nueva (sin 2SV verificada ni passphrase):
 *               el capítulo 1 muestra el gate desde el primer render, sin un
 *               flash de la lista antes de que corra su `setup`.
 */
export function createDemoState(me: string, opts: { fresh?: boolean } = {}): DemoState {
  const marcos = 'marcos.alcala@winclap.com';
  const sofia = 'sofia.dominguez@winclap.com';
  const secrets: DemoSecret[] = [
    secret('demo-meta-latam', 'Meta Business — LATAM', 'accounts@winclap.com', {
      area: 'paidmedia', clientId: 'LATAM', url: 'https://business.facebook.com', hasTotp: true, owner: me, rotatedDaysAgo: 12, tags: ['meta', 'latam'],
      notes: 'Business Manager de LATAM Airlines. Las cuentas publicitarias cuelgan de este BM.',
    }),
    secret('demo-google-despegar', 'Google Ads — Despegar', 'paidmedia@winclap.com', { area: 'paidmedia', clientId: 'Despegar', url: 'https://ads.google.com', hasTotp: true, owner: marcos, rotatedDaysAgo: 30 }),
    secret('demo-tiktok-uber', 'TikTok Ads — Uber', 'paidmedia@winclap.com', { area: 'paidmedia', clientId: 'Uber', url: 'https://ads.tiktok.com', hasTotp: true, owner: me, rotatedDaysAgo: 5 }),
    secret('demo-linkedin-didi', 'LinkedIn Ads — DiDi', 'accounts@winclap.com', { area: 'paidmedia', clientId: 'DiDi', url: 'https://www.linkedin.com/campaignmanager', hasTotp: false, owner: me, rotatedDaysAgo: 21 }),
    secret('demo-semrush', 'Semrush — Growth', 'growth@winclap.com', { area: 'growth', url: 'https://www.semrush.com', hasTotp: false, owner: sofia, rotatedDaysAgo: 97, needsRotation: true }),
    secret('demo-accounts-google', 'accounts@winclap.com — Google', 'accounts@winclap.com', { area: 'business', url: 'https://accounts.google.com', hasTotp: false, owner: me, rotatedDaysAgo: 18, tags: ['cuenta compartida'] }),
    secret('demo-canva', 'Canva Pro — Creative', 'creative@winclap.com', { area: 'creative', url: 'https://www.canva.com', hasTotp: true, owner: sofia, rotatedDaysAgo: 44 }),
    secret('demo-hotjar-elevva', 'Hotjar — Elevva', 'data@winclap.com', { area: 'data', clientId: 'Elevva', url: 'https://insights.hotjar.com', hasTotp: false, owner: marcos, rotatedDaysAgo: 60 }),
  ];
  const g = (secretId: string, principal: string, permission: VaultPermission, grantedBy: string, d: number): VaultGrant => ({
    secretId, principal, permission, grantedBy, grantedAt: daysAgo(d),
  });
  const grants: VaultGrant[] = [
    g('demo-meta-latam', userPrincipal(me), 'owner', me, 52),
    g('demo-meta-latam', userPrincipal(marcos), 'edit', me, 40),
    g('demo-meta-latam', areaGroupPrincipal('paidmedia'), 'read', me, 40),
    g('demo-google-despegar', userPrincipal(marcos), 'owner', marcos, 70),
    g('demo-google-despegar', areaGroupPrincipal('paidmedia'), 'read', marcos, 70),
    g('demo-tiktok-uber', userPrincipal(me), 'owner', me, 45),
    g('demo-tiktok-uber', areaGroupPrincipal('paidmedia'), 'read', me, 45),
    g('demo-linkedin-didi', userPrincipal(me), 'owner', me, 61),
    g('demo-semrush', userPrincipal(sofia), 'owner', sofia, 137),
    g('demo-semrush', userPrincipal(me), 'edit', sofia, 100),
    g('demo-accounts-google', userPrincipal(me), 'owner', me, 58),
    g('demo-accounts-google', userPrincipal(marcos), 'edit', me, 58),
    g('demo-canva', userPrincipal(sofia), 'owner', sofia, 84),
    g('demo-canva', userPrincipal(me), 'read', sofia, 20),
    g('demo-hotjar-elevva', userPrincipal(marcos), 'owner', marcos, 100),
    g('demo-hotjar-elevva', areaGroupPrincipal('data'), 'read', marcos, 100),
    g('demo-hotjar-elevva', userPrincipal(me), 'read', marcos, 9),
  ];
  const ev = (action: VaultAuditAction, actorEmail: string, secretId: string | undefined, hAgo: number, extra: Partial<VaultAuditEvent> = {}): VaultAuditEvent => {
    const ts = hoursAgo(hAgo);
    return {
      auditId: nextId('ev'),
      action,
      actorEmail,
      ts,
      day: ts.slice(0, 10),
      secretId,
      secretName: secretId ? secrets.find((s) => s.secretId === secretId)?.name : undefined,
      ip: DEMO_IP,
      ...extra,
    };
  };
  const activity: VaultAuditEvent[] = [
    ev('secret_revealed', marcos, 'demo-meta-latam', 3, { ip: '190.19.84.21' }),
    ev('totp_read', marcos, 'demo-meta-latam', 3, { ip: '190.19.84.21' }),
    ev('unlock', me, undefined, 26),
    ev('secret_revealed', me, 'demo-tiktok-uber', 26),
    ev('grant_added', me, 'demo-meta-latam', 40 * 24, { targetEmail: marcos }),
    ev('secret_rotated', me, 'demo-tiktok-uber', 5 * 24),
    ev('rotation_flagged', 'system', 'demo-semrush', 7 * 24, { detail: '90 días sin rotar' }),
    ev('link_created', me, 'demo-meta-latam', 2 * 24),
    ev('link_viewed', 'anon', 'demo-meta-latam', 47, { ip: '186.12.44.90' }),
  ];
  const links: DemoLink[] = [
    {
      linkId: 'k39x-q2', secretId: 'demo-meta-latam', secretName: 'Meta Business — LATAM', createdBy: me, createdAt: hoursAgo(48),
      expiresAt: Math.floor((Date.now() - 24 * 3_600_000) / 1000), expiresAtIso: hoursAgo(24), maxViews: 1, viewCount: 1,
      lastViewedAt: hoursAgo(47), lastViewedIp: '186.12.44.90', includesTotp: false,
    },
  ];
  if (opts.fresh) clearVaultUnlockToken();
  return { me, mfaEnrolled: !opts.fresh, hasPassphrase: !opts.fresh, secrets, grants, links, activity };
}

// ─── Permisos efectivos: la MISMA regla que el backend (shared/vault-permissions) ──

/** Quien mira está en Paid Media y Tech (áreas) y maneja el grupo ad-hoc de LATAM. */
function whoAmI(st: DemoState): PrincipalSet {
  return principalsFor(st.me, ['paidmedia', 'tech', 'admin'], ['paidmedia', 'tech'], [{ groupId: 'demo-g1', role: 'manager' }]);
}

const myPrincipals = (st: DemoState) => whoAmI(st).principals;

function myPermission(st: DemoState, secretId: string): VaultPermission | null {
  return effectivePermission(
    st.grants.filter((g) => g.secretId === secretId),
    whoAmI(st)
  );
}

function meta(st: DemoState, s: DemoSecret): VaultSecretMeta {
  const { password: _p, notes: _n, history: _h, ...rest } = s;
  return { ...rest, myPermission: myPermission(st, s.secretId) };
}

function requireUnlocked() {
  if (!getVaultUnlockToken()) throw new Error('VAULT_LOCKED');
}

function find(st: DemoState, secretId: string): DemoSecret {
  const s = st.secrets.find((x) => x.secretId === secretId);
  if (!s) throw new Error('La credencial no existe o no tenés acceso');
  return s;
}

function log(st: DemoState, action: VaultAuditAction, secretId?: string, extra: Partial<VaultAuditEvent> = {}) {
  const ts = new Date().toISOString();
  st.activity.unshift({
    auditId: nextId('ev'),
    action,
    actorEmail: st.me,
    ts,
    day: ts.slice(0, 10),
    secretId,
    secretName: secretId ? st.secrets.find((s) => s.secretId === secretId)?.name : undefined,
    ip: DEMO_IP,
    ...extra,
  });
}

/** Código TOTP de demo: estable dentro de la ventana de 30 s, distinto entre ventanas. */
function demoTotp(secretId: string) {
  const step = VAULT_POLICY.revealSeconds; // 30 s, como el TOTP real
  const win = Math.floor(Date.now() / 1000 / step);
  let h = 2166136261;
  for (const ch of `${secretId}:${win}`) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const code = String((h >>> 0) % 1_000_000).padStart(6, '0');
  return { code, expiresIn: step - (Math.floor(Date.now() / 1000) % step), digits: 6 };
}


const linkView = (l: DemoLink) => ({ ...l, url: `${window.location.origin}/vault/s/${l.linkId}`, status: linkStatus(l) });

// ─── Operaciones del guion ────────────────────────────────────────────────────

export function applyDemoOp(st: DemoState, op: DemoOp): void {
  switch (op) {
    case 'access.fresh':
      st.mfaEnrolled = false;
      st.hasPassphrase = false;
      clearVaultUnlockToken();
      break;
    case 'access.ready':
      st.mfaEnrolled = true;
      st.hasPassphrase = true;
      break;
    case 'mfa.enroll':
      st.mfaEnrolled = true;
      break;
    case 'session.lock':
      clearVaultUnlockToken();
      break;
    case 'session.unlock':
      setVaultUnlockToken(DEMO_UNLOCK_TOKEN, VAULT_POLICY.unlockMinutes);
      break;
  }
}

// ─── Handlers por procedure ───────────────────────────────────────────────────

type Handler = (st: DemoState, input: unknown) => unknown;
type In<T> = T;

const handlers: Record<string, Handler> = {
  'profile.getMfaStatus': (st) => ({ enrolled: st.mfaEnrolled, enforceFrom: null, enforcedNow: false, daysUntil: null }),

  'vault.getMyAccess': (st) => ({
    canView: true,
    isAdmin: false,
    mfaEnrolled: st.mfaEnrolled,
    hasPassphrase: st.hasPassphrase,
    lockedUntil: null,
    principals: myPrincipals(st),
  }),
  'vault.setupPassphrase': (st) => {
    st.hasPassphrase = true;
    log(st, 'passphrase_set');
    return { unlockToken: DEMO_UNLOCK_TOKEN, expiresInMinutes: VAULT_POLICY.unlockMinutes };
  },
  'vault.unlock': (st) => {
    log(st, 'unlock');
    return { unlockToken: DEMO_UNLOCK_TOKEN, expiresInMinutes: VAULT_POLICY.unlockMinutes };
  },
  'vault.changePassphrase': () => ({ unlockToken: DEMO_UNLOCK_TOKEN, expiresInMinutes: VAULT_POLICY.unlockMinutes }),

  'vault.listSecrets': (st) => {
    const items = st.secrets.map((s) => meta(st, s)).filter((m) => m.myPermission !== null);
    return {
      items,
      stats: {
        total: items.length,
        needsRotation: items.filter((i) => i.needsRotation).length,
        withoutTotp: items.filter((i) => !i.hasTotp).length,
        owned: items.filter((i) => i.myPermission === 'owner').length,
        sharedAccounts: 49,
      },
    };
  },
  'vault.getSecret': (st, input) => {
    const { secretId } = input as In<{ secretId: string }>;
    const s = find(st, secretId);
    const days = Math.round((new Date(s.lastRotatedAt).getTime() + VAULT_POLICY.rotationDays * 86_400_000 - Date.now()) / 86_400_000);
    return {
      meta: meta(st, s),
      grants: st.grants.filter((g) => g.secretId === secretId),
      hasNotes: !!s.notes,
      totp: s.hasTotp ? { digits: 6, step: 30 } : null,
      recentActivity: st.activity.filter((e) => e.secretId === secretId).slice(0, 8),
      rotationDueInDays: s.needsRotation ? Math.min(days, 0) : days,
    };
  },
  'vault.revealSecret': (st, input) => {
    requireUnlocked();
    const s = find(st, (input as In<{ secretId: string }>).secretId);
    log(st, 'secret_revealed', s.secretId);
    return { password: s.password, notes: s.notes };
  },
  'vault.getTotp': (st, input) => {
    requireUnlocked();
    const s = find(st, (input as In<{ secretId: string }>).secretId);
    log(st, 'totp_read', s.secretId);
    return demoTotp(s.secretId);
  },
  'vault.getSecretActivity': (st, input) => ({
    items: st.activity.filter((e) => e.secretId === (input as In<{ secretId: string }>).secretId),
    cursor: null,
  }),
  'vault.getMyActivity': (st) => ({ items: st.activity.filter((e) => e.actorEmail === st.me), cursor: null }),
  'vault.updateSecret': (st, input) => {
    const p = input as In<{ secretId: string; name?: string; username?: string; url?: string | null; area?: string | null; clientId?: string | null; tags?: string[]; notes?: string | null; totpSeed?: string | null }>;
    const s = find(st, p.secretId);
    if (p.name) s.name = p.name;
    if (p.username) s.username = p.username;
    if (p.url !== undefined) s.url = p.url ?? undefined;
    if (p.area !== undefined) s.area = p.area ?? undefined;
    if (p.clientId !== undefined) s.clientId = p.clientId ?? undefined;
    if (p.tags) s.tags = p.tags;
    if (p.notes !== undefined) s.notes = p.notes;
    if (p.totpSeed !== undefined) s.hasTotp = p.totpSeed !== null;
    s.version += 1;
    s.updatedAt = new Date().toISOString();
    s.updatedBy = st.me;
    log(st, 'secret_updated', s.secretId);
    return meta(st, s);
  },
  'vault.rotateSecret': (st, input) => {
    requireUnlocked();
    const p = input as In<{ secretId: string; newPassword: string }>;
    const s = find(st, p.secretId);
    s.history.unshift({ version: s.version, rotatedAt: s.lastRotatedAt, rotatedBy: s.updatedBy, password: s.password });
    s.password = p.newPassword;
    s.version += 1;
    s.lastRotatedAt = new Date().toISOString();
    s.updatedAt = s.lastRotatedAt;
    s.updatedBy = st.me;
    s.needsRotation = false;
    log(st, 'secret_rotated', s.secretId);
    return meta(st, s);
  },
  'vault.listSecretVersions': (st, input) => {
    const s = find(st, (input as In<{ secretId: string }>).secretId);
    return s.history.map((h) => ({ version: h.version, rotatedAt: h.rotatedAt, rotatedBy: h.rotatedBy, canView: true }));
  },
  'vault.revealSecretVersion': (st, input) => {
    requireUnlocked();
    const p = input as In<{ secretId: string; version: number }>;
    const h = find(st, p.secretId).history.find((x) => x.version === p.version);
    if (!h) throw new Error('Versión inexistente');
    return { password: h.password, rotatedAt: h.rotatedAt, rotatedBy: h.rotatedBy };
  },
  'vault.deleteSecret': () => {
    throw new Error('En el workshop no se borran credenciales');
  },
  'vault.grantAccess': (st, input) => {
    const p = input as In<{ secretId: string; principal: string; permission: VaultPermission }>;
    find(st, p.secretId);
    const grant: VaultGrant = { ...p, grantedBy: st.me, grantedAt: new Date().toISOString() };
    st.grants = [...st.grants.filter((g) => !(g.secretId === p.secretId && g.principal === p.principal)), grant];
    log(st, 'grant_added', p.secretId, { targetEmail: p.principal.replace(/^user#/, '') });
    return grant;
  },
  'vault.revokeAccess': (st, input) => {
    const p = input as In<{ secretId: string; principal: string }>;
    st.grants = st.grants.filter((g) => !(g.secretId === p.secretId && g.principal === p.principal));
    find(st, p.secretId).needsRotation = true;
    log(st, 'grant_revoked', p.secretId, { targetEmail: p.principal.replace(/^user#/, '') });
    return { ok: true };
  },

  'vault.listGroups': (st) => {
    const area = (['paidmedia', 'ownedmedia', 'creative', 'data', 'tech', 'growth', 'business'] as const).map((group, i) => ({
      group,
      label: VAULT_AREA_LABEL[group] ?? group,
      principal: areaGroupPrincipal(group),
      members: [14, 9, 11, 6, 5, 4, 7][i],
      secrets: st.grants.filter((g) => g.principal === areaGroupPrincipal(group)).length,
    }));
    return {
      area,
      adhoc: [
        {
          groupId: 'demo-g1', name: 'LATAM — Paid Media', description: 'Equipo de la cuenta', responsibleEmail: st.me, createdBy: st.me, createdAt: daysAgo(30), // i18n-ok: datos de demo del workshop
          principal: 'group#adhoc:demo-g1', memberCount: 4, secrets: 2, myRole: 'manager', canManage: true,
        },
        {
          groupId: 'demo-g2', name: 'Freelancers Creative Q4', description: 'Diseñadores externos, hasta diciembre', responsibleEmail: 'sofia.dominguez@winclap.com', createdBy: 'sofia.dominguez@winclap.com', createdAt: daysAgo(12), // i18n-ok: datos de demo del workshop
          principal: 'group#adhoc:demo-g2', memberCount: 3, secrets: 1, myRole: 'reader', canManage: false,
        },
      ],
    };
  },
  'vault.getGroup': (st, input) => {
    const { groupId } = input as In<{ groupId: string }>;
    const mine = groupId === 'demo-g1';
    return {
      group: { groupId, name: mine ? 'LATAM — Paid Media' : 'Freelancers Creative Q4', responsibleEmail: mine ? st.me : 'sofia.dominguez@winclap.com', createdBy: st.me, createdAt: daysAgo(30) },
      principal: `group#adhoc:${groupId}`,
      members: [
        { groupId, email: st.me, role: 'manager', addedBy: st.me, addedAt: daysAgo(30) },
        { groupId, email: 'marcos.alcala@winclap.com', role: 'editor', addedBy: st.me, addedAt: daysAgo(30) },
        { groupId, email: 'lucia.perez@winclap.com', role: 'reader', addedBy: st.me, addedAt: daysAgo(10) },
      ],
      canManage: mine,
      secrets: [{ secretId: 'demo-meta-latam', name: 'Meta Business — LATAM', username: 'accounts@winclap.com', area: 'paidmedia', permission: 'edit' }],
    };
  },

  'vault.createShareLink': (st, input) => {
    requireUnlocked();
    const p = input as In<{ secretId: string; expiresInHours: number; oneTime: boolean; includeTotp: boolean }>;
    const s = find(st, p.secretId);
    const linkId = nextId('lnk');
    const expiresAtIso = new Date(Date.now() + p.expiresInHours * 3_600_000).toISOString();
    st.links.unshift({
      linkId, secretId: s.secretId, secretName: s.name, createdBy: st.me, createdAt: new Date().toISOString(),
      expiresAt: Math.floor(Date.parse(expiresAtIso) / 1000), expiresAtIso, maxViews: p.oneTime ? 1 : null, viewCount: 0, includesTotp: p.includeTotp,
    });
    log(st, 'link_created', s.secretId, { linkId });
    return { linkId, url: `${window.location.origin}/vault/s/${linkId}`, expiresAt: expiresAtIso, oneTime: p.oneTime, includesTotp: p.includeTotp };
  },
  'vault.listSecretLinks': (st, input) => st.links.filter((l) => l.secretId === (input as In<{ secretId: string }>).secretId).map(linkView),
  'vault.listMyLinks': (st) => st.links.filter((l) => l.createdBy === st.me).map(linkView),
  'vault.revokeShareLink': (st, input) => {
    const l = st.links.find((x) => x.linkId === (input as In<{ linkId: string }>).linkId);
    if (l) {
      l.revokedAt = new Date().toISOString();
      l.revokedBy = st.me;
      log(st, 'link_revoked', l.secretId, { linkId: l.linkId });
    }
    return { ok: true };
  },
  'vault.getMyCensusAssignments': () => [],
  // El asistente de MFA responde acá sin Bedrock: una respuesta fija por idioma (el flujo real es el mismo).
  'vault.mfaAssistantAsk': (_st, input) => {
    const lang = (input as In<{ lang?: string }>).lang ?? 'es';
    const answers: Record<string, string> = {
      es: 'En Meta: **Configuración** → **Centro de cuentas** → **Contraseña y seguridad** → **Autenticación en dos pasos** → elegí la cuenta → **App de autenticación**. Debajo del QR está el enlace *¿No podés escanear?*: ahí aparece la clave para pegar acá.', // i18n-ok: contenido de la demo, no UI
      en: 'On Meta: **Settings** → **Accounts Center** → **Password and security** → **Two-factor authentication** → pick the account → **Authentication app**. Under the QR there is a *Can\'t scan?* link: that is the key to paste here.', // i18n-ok: contenido de la demo, no UI
      pt: 'No Meta: **Configurações** → **Central de Contas** → **Senha e segurança** → **Autenticação de dois fatores** → escolha a conta → **App de autenticação**. Abaixo do QR está o link *Não consegue escanear?*: ali aparece a chave para colar aqui.', // i18n-ok: contenido de la demo, no UI
    };
    return { answer: answers[lang] ?? answers.es };
  },
  'vault.listMembersForPicker': (st) => [
    { email: 'lucia.perez@winclap.com', name: 'Lucía Pérez', role: 'Paid Media Specialist', area: 'Paid Media', team: 'LATAM', status: 'Activo' },
    { email: 'marcos.alcala@winclap.com', name: 'Marcos Alcalá', role: 'Paid Media Lead', area: 'Paid Media', team: 'LATAM', status: 'Activo' },
    { email: 'sofia.dominguez@winclap.com', name: 'Sofía Domínguez', role: 'Growth Manager', area: 'Growth', team: 'Growth', status: 'Activo' },
    { email: 'julian.rossi@winclap.com', name: 'Julián Rossi', role: 'Data Analyst', area: 'Data', team: 'Data', status: 'Activo' },
    { email: 'camila.torres@winclap.com', name: 'Camila Torres', role: 'Creative Director', area: 'Creative', team: 'Creative', status: 'Activo' },
    { email: st.me, name: st.me.split('@')[0].replace('.', ' '), role: 'Vos', area: 'Paid Media', team: 'LATAM', status: 'Activo' },
  ],
};

/** true si el workshop responde este procedure sin ir al backend. */
export const isDemoHandled = (path: string) => path.startsWith('vault') || path in handlers;

export function handleDemo(st: DemoState, path: string, input: unknown): unknown {
  const h = handlers[path];
  if (!h) throw new Error(`El workshop no simula ${path}`);
  return h(st, input);
}
